import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { CPDCInfoButton } from "@/components/ui/MetricInfo";
import { toast } from "sonner";
import { 
  Megaphone, Plus, Archive, Edit, Play, Pause, Loader2, Eye, DollarSign, 
  MoreHorizontal, MapPin, Target, Clock, Image, Upload, X, Save, AlertTriangle,
  Library, Check, ImagePlus, Gift, Ticket, Copy, Search, Building2, Map, Tag, Sparkles
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown, ChevronUp, Calendar, CreditCard } from "lucide-react";
import { CampaignPaymentModal } from "@/components/CampaignPaymentModal";

const surfaces = [
  { value: "PASSENGER_APP", label: "Passenger App" },
  { value: "DRIVER_APP", label: "Driver App" },
  { value: "PANEL", label: "Drive Panel" },
];

const statusColors = {
  DRAFT: "status-draft",
  ACTIVE: "status-active",
  PAUSED: "status-paused",
  COMPLETED: "status-completed",
  PAYMENT_FAILED: "status-payment-failed"
};

const statusLabels = {
  PAYMENT_FAILED: "Payment Required"
};

const daysOfWeek = [
  { value: "monday", label: "Mon" },
  { value: "tuesday", label: "Tue" },
  { value: "wednesday", label: "Wed" },
  { value: "thursday", label: "Thu" },
  { value: "friday", label: "Fri" },
  { value: "saturday", label: "Sat" },
  { value: "sunday", label: "Sun" },
];

const timeSlots = [
  { value: "morning", label: "Morning (6am-12pm)" },
  { value: "afternoon", label: "Afternoon (12pm-6pm)" },
  { value: "evening", label: "Evening (6pm-10pm)" },
  { value: "night", label: "Night (10pm-6am)" },
];

const ageRanges = [
  { value: "18-24", label: "18-24" },
  { value: "25-34", label: "25-34" },
  { value: "35-44", label: "35-44" },
  { value: "45-54", label: "45-54" },
  { value: "55+", label: "55+" },
];

const venueTypes = [
  { value: "restaurant", label: "Restaurant" },
  { value: "retail", label: "Retail" },
  { value: "entertainment", label: "Entertainment" },
  { value: "services", label: "Services" },
  { value: "nightlife", label: "Nightlife" },
  { value: "airport", label: "Airport" },
  { value: "sporting_facility", label: "Sporting Facility" },
  { value: "university", label: "University" },
  { value: "hospital", label: "Hospital" },
  { value: "shopping_centre", label: "Shopping Centre" },
];

const interestOptions = [
  { value: "food", label: "Food & Dining" },
  { value: "shopping", label: "Shopping" },
  { value: "travel", label: "Travel" },
  { value: "fitness", label: "Fitness" },
  { value: "technology", label: "Technology" },
  { value: "entertainment", label: "Entertainment" },
];

// Draft storage key
const CAMPAIGN_DRAFT_KEY = 'booma_campaign_draft';

// Fallback tier config used while loading from API. Prices mirror backend
// CAMPAIGN_TIER_CONFIG in models/enums.py. UI derives per-duration price as
// price_monthly × duration_months, so no nested pricing dict is needed.
const DEFAULT_TIER_CONFIG = {
  BRONZE: { name: "Bronze", price_monthly: 50, max_campaigns_per_account: 1, allowed_durations: ["12_months"], display_priority: 1, enabled: true, features: { cpm_enabled: true, cpc_enabled: false, cpdc_enabled: false, deals_enabled: false, basic_targeting: false, advanced_targeting: false, animated_creatives: false, pause_enabled: false }, description: "Views only - great for brand awareness", tagline: "Standard Impression Model", is_free: false },
  SILVER: { name: "Silver", price_monthly: 199, max_campaigns_per_account: 3, allowed_durations: ["1_month", "3_months", "6_months", "12_months"], display_priority: 2, enabled: false, features: { cpm_enabled: true, cpc_enabled: true, cpdc_enabled: true, deals_enabled: true, basic_targeting: true, advanced_targeting: false, animated_creatives: false, pause_enabled: false }, description: "Views + Engagement with delivered customers", tagline: "Pay for Engagement", is_free: false },
  GOLD: { name: "Gold", price_monthly: 499, max_campaigns_per_account: 5, allowed_durations: ["1_month", "3_months", "6_months", "12_months"], display_priority: 3, enabled: false, features: { cpm_enabled: true, cpc_enabled: true, cpdc_enabled: true, deals_enabled: true, basic_targeting: true, advanced_targeting: true, animated_creatives: true, pause_enabled: true }, description: "Intent targeting with AI and full flexibility", tagline: "Intent Targeting", is_free: false },
};

// Default campaign state
const getDefaultCampaign = () => ({
  name: "",
  description: "",
  tier: "BRONZE",  // Campaign-level tier
  surfaces: ["PASSENGER_APP", "PANEL"],
  start_at: "",
  end_at: "",
  duration: "",  // Fixed duration: 1_month, 3_months, 6_months, 12_months
  budget_limit: "",
  pricing: {
    cpm_enabled: true,
    cpc_enabled: false,
    cpdc_enabled: false,  // Single toggle for all CPDC types
    cpdc_direct_enabled: false,
    cpdc_proximity_enabled: false,
    cpdc_prompted_enabled: false
  },
  creative_ids: [],
  selectedBanner: null,
  selectedPanel: null,
  // Deal configuration
  deal_available: false,
  deal_description: "",
  offer_text: "",
  cta_text: "Claim Deal",
  targeting: {
    location_ids: [],
    states: [],
    postcodes: [],
    suburbs: [],
    radius_override: null,
    days: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
    time_slots: ["morning", "afternoon", "evening", "night"],
    gender: "all",
    age_ranges: ["all"],  // Multi-select array
    venue_types: ["any"],  // Multi-select array
    interests: ["any"],  // Multi-select array
    enabled: true
  }
});

export default function Campaigns() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [locations, setLocations] = useState([]);
  const [globalPricing, setGlobalPricing] = useState(null);
  const [CAMPAIGN_TIER_CONFIG, setCampaignTierConfig] = useState(DEFAULT_TIER_CONFIG);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [creativePickerOpen, setCreativePickerOpen] = useState(false);
  const [creativePickerType, setCreativePickerType] = useState(null); // 'BANNER' or 'PANEL'
  
  // Draft state
  const [hasDraft, setHasDraft] = useState(false);
  const [draftRestoredToast, setDraftRestoredToast] = useState(false);
  
  // Tier details expanded state (single toggle for all tiers)
  const [tiersExpanded, setTiersExpanded] = useState(false);
  
  // Archive confirmation modal state
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [campaignToArchive, setCampaignToArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);

  // TKT-82: Payment modal state (for campaign save prepayment & retry)
  const [paymentModal, setPaymentModal] = useState({
    open: false,
    clientSecret: null,
    amount: 0,
    campaignName: "",
  });
  
  // Location targeting state
  const [locationTargetingOpen, setLocationTargetingOpen] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");
  const [locationSearchResults, setLocationSearchResults] = useState([]);
  const [searchingLocations, setSearchingLocations] = useState(false);
  const [australianStates, setAustralianStates] = useState([]);
  
  // AI Deal Suggestion state
  const [suggestingDeal, setSuggestingDeal] = useState(false);
  const [dealSuggestions, setDealSuggestions] = useState([]);
  const [suggestionPopoverOpen, setSuggestionPopoverOpen] = useState(false);
  
  // Confirm save dialog
  
  const [newCampaign, setNewCampaign] = useState(getDefaultCampaign());
  const [saving, setSaving] = useState(false);

  // Track if draft has been restored in this session to prevent duplicate toasts
  const draftRestoredRef = useRef(false);
  // Track if this is the first render or if we're returning from another page
  const isFirstRenderRef = useRef(true);

  // Check for existing draft - runs when component mounts or when navigating back to this page
  useEffect(() => {
    // On first render, reset the restoration flag to allow checking
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      draftRestoredRef.current = false;
    }
    
    // Already restored in this session
    if (draftRestoredRef.current) return;
    // User already has form open
    if (isCreating) return;
    
    const savedDraft = localStorage.getItem(CAMPAIGN_DRAFT_KEY);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        // Check if draft has meaningful content
        if (draft.name || draft.description || draft.selectedBannerId || draft.selectedPanelId || 
            draft.start_at || draft.end_at || draft.budget_limit) {
          
          // Mark as restored before setting state to prevent re-runs in this session
          draftRestoredRef.current = true;
          
          // Restore creative objects from IDs (will be null if creatives haven't loaded yet)
          const restoredDraft = {
            ...draft,
            selectedBanner: draft.selectedBannerId ? creatives.find(c => c.id === draft.selectedBannerId) || null : null,
            selectedPanel: draft.selectedPanelId ? creatives.find(c => c.id === draft.selectedPanelId) || null : null
          };
          
          setNewCampaign(restoredDraft);
          setHasDraft(true);
          setIsCreating(true);
          
          // Show toast notification after a brief delay
          setTimeout(() => {
            toast.success("Draft campaign restored", {
              description: "Your previous progress has been recovered",
              duration: 4000
            });
          }, 500);
        }
      } catch (e) {
        console.error("Failed to parse campaign draft:", e);
        localStorage.removeItem(CAMPAIGN_DRAFT_KEY);
      }
    }
  }, [creatives, isCreating]); // Re-run when creatives load or isCreating changes
  
  // Reset flags when component unmounts (navigating away)
  useEffect(() => {
    return () => {
      isFirstRenderRef.current = true;
    };
  }, []);

  // Helper to create a lightweight draft for storage (no large image data)
  const createLightweightDraft = (campaign) => {
    return {
      ...campaign,
      // Store only IDs, not full creative objects with base64 images
      selectedBanner: null,
      selectedPanel: null,
      selectedBannerId: campaign.selectedBanner?.id || null,
      selectedPanelId: campaign.selectedPanel?.id || null
    };
  };

  // Auto-save draft when newCampaign changes (debounced)
  useEffect(() => {
    if (!isCreating) return;
    
    const timer = setTimeout(() => {
      // Only save if there's meaningful content
      if (newCampaign.name || newCampaign.description || newCampaign.selectedBanner || 
          newCampaign.selectedPanel || newCampaign.start_at || newCampaign.end_at || 
          newCampaign.budget_limit) {
        try {
          const lightweightDraft = createLightweightDraft(newCampaign);
          localStorage.setItem(CAMPAIGN_DRAFT_KEY, JSON.stringify(lightweightDraft));
          setHasDraft(true);
        } catch (e) {
          console.error("Failed to save draft:", e);
        }
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [newCampaign, isCreating]);

  // Clear draft when successfully saved or explicitly discarded
  const clearDraft = () => {
    localStorage.removeItem(CAMPAIGN_DRAFT_KEY);
    setHasDraft(false);
  };

  // Save draft and navigate to creatives
  const saveDraftAndGoToCreatives = () => {
    if (newCampaign.name || newCampaign.description || newCampaign.selectedBanner || 
        newCampaign.selectedPanel || newCampaign.start_at || newCampaign.end_at || 
        newCampaign.budget_limit) {
      try {
        const lightweightDraft = createLightweightDraft(newCampaign);
        localStorage.setItem(CAMPAIGN_DRAFT_KEY, JSON.stringify(lightweightDraft));
        toast.info("Draft saved", { 
          description: "Your campaign progress has been saved",
          duration: 3000 
        });
      } catch (e) {
        console.error("Failed to save draft:", e);
      }
    }
    navigate('/creatives');
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [campaignsRes, creativesRes, locationsRes, pricingRes, tierConfigRes, statesRes, accountRes] = await Promise.all([
        axios.get(`${API}/campaigns`, { headers }),
        axios.get(`${API}/creatives`, { headers }),
        axios.get(`${API}/locations`, { headers }),
        axios.get(`${API}/settings/pricing`, { headers }),
        axios.get(`${API}/settings/tier-config`, { headers }).catch(() => ({ data: { tiers: DEFAULT_TIER_CONFIG } })),
        axios.get(`${API}/targeting/states`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/accounts/me`, { headers }).catch(() => ({ data: null }))
      ]);
      setCampaigns(campaignsRes.data);
      setCreatives(creativesRes.data);
      setLocations(locationsRes.data);
      setGlobalPricing(pricingRes.data);
      if (tierConfigRes.data?.tiers) setCampaignTierConfig(tierConfigRes.data.tiers);
      setAustralianStates(statesRes.data || []);
      setAccount(accountRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Search for suburbs/postcodes
  const searchLocations = async (query) => {
    if (!query || query.length < 2) {
      setLocationSearchResults([]);
      return;
    }
    
    setSearchingLocations(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/targeting/search-locations`, {
        headers,
        params: { query }
      });
      setLocationSearchResults(response.data || []);
    } catch (error) {
      console.error("Location search error:", error);
      setLocationSearchResults([]);
    } finally {
      setSearchingLocations(false);
    }
  };

  // Debounced location search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (locationSearch) {
        searchLocations(locationSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [locationSearch]);

  // Get today's date in YYYY-MM-DD format for min date validation
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const handleSaveCampaign = async () => {
    if (!newCampaign.name.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }
    
    // Validate duration is selected
    if (!newCampaign.duration) {
      toast.error("Please select a campaign duration");
      return;
    }
    
    // Validate required dates
    if (!newCampaign.start_at) {
      toast.error("Start date is required");
      return;
    }
    if (!newCampaign.end_at) {
      toast.error("End date is required");
      return;
    }
    
    // Validate start date is not before today (for new campaigns)
    const today = getTodayDate();
    if (newCampaign.start_at < today) {
      toast.error("Start date cannot be before today");
      return;
    }
    
    // Validate end date is after start date
    if (newCampaign.end_at < newCampaign.start_at) {
      toast.error("End date must be after start date");
      return;
    }
    
    setSaving(true);
    try {
      // Collect creative IDs from selected creatives
      const creativeIds = [];
      if (newCampaign.selectedBanner) creativeIds.push(newCampaign.selectedBanner.id);
      if (newCampaign.selectedPanel) creativeIds.push(newCampaign.selectedPanel.id);
      
      // Build the campaign payload
      const payload = {
        name: newCampaign.name,
        description: newCampaign.description,
        tier: newCampaign.tier,  // Campaign-level tier selection
        surfaces: newCampaign.surfaces,
        start_at: newCampaign.start_at || null,
        end_at: newCampaign.end_at || null,
        duration: newCampaign.duration || null,  // Fixed duration
        budget_limit: newCampaign.budget_limit ? parseFloat(newCampaign.budget_limit) : null,
        pricing: newCampaign.pricing,
        creative_ids: creativeIds,
        // Deal configuration
        deal_available: newCampaign.deal_available,
        deal_description: newCampaign.deal_description || null,
        offer_text: newCampaign.offer_text || null,
        cta_text: newCampaign.cta_text || null,
        targeting_config: newCampaign.targeting.enabled ? {
          location_ids: newCampaign.targeting.location_ids,
          states: newCampaign.targeting.states,
          postcodes: newCampaign.targeting.postcodes,
          suburbs: newCampaign.targeting.suburbs,
          radius_override: newCampaign.targeting.radius_override,
          days: newCampaign.targeting.days,
          time_slots: newCampaign.targeting.time_slots,
          gender: newCampaign.targeting.gender,
          age_ranges: newCampaign.targeting.age_ranges,
          venue_types: newCampaign.targeting.venue_types,
          interests: newCampaign.targeting.interests
        } : null
      };
      
      const response = await axios.post(`${API}/campaigns`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { campaign, client_secret } = response.data || {};
      setIsCreating(false);
      setConfirmSaveOpen(false);
      clearDraft(); // Clear the draft on successful save
      resetNewCampaign();
      fetchData();

      if (client_secret) {
        // TKT-82: open payment modal; webhook will promote campaign to ACTIVE on success.
        setPaymentModal({
          open: true,
          clientSecret: client_secret,
          amount: Number(campaign?.tier_cost || 0),
          campaignName: campaign?.name || payload.name,
        });
      } else {
        toast.success("Campaign created successfully");
      }
    } catch (error) {
      // Handle Pydantic validation errors (422) which return an array
      const errorData = error.response?.data;
      let errorMessage = "Failed to create campaign";
      
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation errors
          errorMessage = errorData.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.msg || errorData.detail.message || JSON.stringify(errorData.detail);
        }
      }
      
      toast.error(errorMessage);
      console.error("Campaign save error:", errorData);
    } finally {
      setSaving(false);
    }
  };

  const openCreativePicker = (type) => {
    setCreativePickerType(type);
    setCreativePickerOpen(true);
  };

  const selectCreative = (creative) => {
    if (creativePickerType === 'BANNER') {
      setNewCampaign(prev => ({ ...prev, selectedBanner: creative }));
    } else {
      setNewCampaign(prev => ({ ...prev, selectedPanel: creative }));
    }
    setCreativePickerOpen(false);
  };

  const handleActivate = async (campaignId) => {
    try {
      await axios.post(`${API}/campaigns/${campaignId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Campaign activated");
      fetchData();
    } catch (error) {
      toast.error("Failed to activate campaign");
    }
  };

  // TKT-82: Retry payment for a PAYMENT_FAILED campaign — opens the payment modal.
  const handleRetryPayment = async (campaign) => {
    try {
      const response = await axios.post(
        `${API}/campaigns/${campaign.id}/retry-payment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPaymentModal({
        open: true,
        clientSecret: response.data.client_secret,
        amount: Number(campaign.tier_cost || 0),
        campaignName: campaign.name,
      });
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to start payment retry";
      toast.error(msg);
    }
  };

  const handlePause = async (campaignId) => {
    try {
      await axios.post(`${API}/campaigns/${campaignId}/pause`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Campaign paused");
      fetchData();
    } catch (error) {
      // Show specific error message for Gold tier restriction
      const errorMsg = error.response?.data?.detail || "Failed to pause campaign";
      toast.error(errorMsg);
    }
  };

  // AI Deal Suggestion
  const suggestDeal = async () => {
    setSuggestingDeal(true);
    try {
      const response = await axios.post(`${API}/campaigns/suggest-deal`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDealSuggestions(response.data.suggestions || []);
      setSuggestionPopoverOpen(true);
      
      if (response.data.fallback) {
        toast.info("Using fallback suggestions", {
          description: "AI service temporarily unavailable"
        });
      }
    } catch (error) {
      console.error("Failed to get deal suggestions:", error);
      toast.error("Failed to generate suggestions");
    } finally {
      setSuggestingDeal(false);
    }
  };

  const applyDealSuggestion = (suggestion) => {
    setNewCampaign(prev => ({
      ...prev,
      deal_description: suggestion.deal_description,
      offer_text: suggestion.offer_text,
      cta_text: suggestion.cta_text
    }));
    setSuggestionPopoverOpen(false);
    toast.success("Deal suggestion applied", {
      description: "Feel free to edit the text to match your offer"
    });
  };

  // Open archive confirmation modal
  const openArchiveModal = (campaign) => {
    setCampaignToArchive(campaign);
    setArchiveModalOpen(true);
  };

  // Confirm archive action
  const confirmArchive = async () => {
    if (!campaignToArchive) return;
    
    setArchiving(true);
    try {
      await axios.delete(`${API}/campaigns/${campaignToArchive.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Campaign archived successfully");
      setArchiveModalOpen(false);
      setCampaignToArchive(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to archive campaign");
    } finally {
      setArchiving(false);
    }
  };

  const resetNewCampaign = () => {
    setNewCampaign(getDefaultCampaign());
  };

  // Discard draft and reset form
  const discardDraft = () => {
    clearDraft();
    resetNewCampaign();
    setIsCreating(false);
    toast.info("Draft discarded");
  };

  // Helper to get location targeting summary
  const getLocationTargetingSummary = () => {
    const parts = [];
    const t = newCampaign.targeting;
    
    if (t.location_ids.length > 0) {
      parts.push(`${t.location_ids.length} venue${t.location_ids.length > 1 ? 's' : ''}`);
    }
    if (t.states.length > 0) {
      parts.push(`${t.states.length} state${t.states.length > 1 ? 's' : ''}`);
    }
    if (t.postcodes.length > 0) {
      parts.push(`${t.postcodes.length} postcode${t.postcodes.length > 1 ? 's' : ''}`);
    }
    if (t.suburbs.length > 0) {
      parts.push(`${t.suburbs.length} suburb${t.suburbs.length > 1 ? 's' : ''}`);
    }
    
    return parts.length > 0 ? parts.join(', ') : 'All Areas';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading campaigns...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Campaigns</h1>
          <p className="text-muted-foreground mt-1">
            Create and manage advertising campaigns with targeting
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Campaigns</p>
                <p className="text-2xl font-bold">{campaigns.length}</p>
              </div>
              <Megaphone className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-green-500">
                  {campaigns.filter(c => c.status === "ACTIVE").length}
                </p>
              </div>
              <Play className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Paused <span className="text-xs">(Gold Campaigns)</span>
                </p>
                <p className="text-2xl font-bold text-amber-500">
                  {campaigns.filter(c => c.status === "PAUSED").length}
                </p>
              </div>
              <Pause className="h-8 w-8 opacity-50 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Draft</p>
                <p className="text-2xl font-bold text-muted-foreground">
                  {campaigns.filter(c => c.status === "DRAFT").length}
                </p>
              </div>
              <Edit className="h-8 w-8 text-muted-foreground opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaign Widgets - Full Width */}
      <div className="space-y-4">
        {campaigns.length === 0 ? (
          <Card className="bg-card border-border border-dashed">
            <CardContent className="p-12 text-center">
              <Megaphone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">No Campaigns Yet</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Create your first campaign to start advertising
              </p>
            </CardContent>
          </Card>
        ) : (
          campaigns.map(campaign => {
            const campaignCreatives = creatives.filter(c => campaign.creative_ids?.includes(c.id));
            const targeting = campaign.targeting_config || {};
            const pricing = campaign.pricing || {};
            const campaignTier = campaign.tier || "BRONZE";
            const tierConfig = CAMPAIGN_TIER_CONFIG[campaignTier] || CAMPAIGN_TIER_CONFIG.BRONZE;
            
            return (
              <Card 
                key={campaign.id} 
                className="bg-card border-border hover:border-primary/50 transition-all hover:shadow-lg group"
                data-testid={`campaign-widget-${campaign.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex gap-6">
                    {/* Left Section - Campaign Info & Creatives */}
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={statusColors[campaign.status]}>
                              {statusLabels[campaign.status] || campaign.status}
                            </Badge>
                            <Badge className={`${
                              campaignTier === 'GOLD' ? 'bg-amber-500/20 text-amber-600 border-amber-500/30' :
                              campaignTier === 'SILVER' ? 'bg-slate-400/20 text-slate-600 border-slate-400/30' :
                              'bg-orange-700/20 text-orange-700 border-orange-700/30'
                            }`}>
                              {campaignTier}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {campaign.surfaces?.map(s => s.replace("_", " ")).join(" • ")}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold">{campaign.name}</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {campaign.description || "No description"}
                          </p>
                          {/* Effective Date Range - Prominent Display */}
                          {(campaign.start_at || campaign.end_at) && (() => {
                            const now = new Date();
                            const endDate = campaign.end_at ? new Date(campaign.end_at) : null;
                            const startDate = campaign.start_at ? new Date(campaign.start_at) : null;
                            const isExpired = endDate && endDate < now;
                            const isNotStarted = startDate && startDate > now;
                            const daysRemaining = endDate ? Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)) : null;
                            
                            return (
                              <div className={`mt-3 p-2 rounded-lg border ${
                                isExpired ? 'bg-red-500/10 border-red-500/30' : 
                                isNotStarted ? 'bg-blue-500/10 border-blue-500/30' :
                                daysRemaining && daysRemaining <= 7 ? 'bg-amber-500/10 border-amber-500/30' : 
                                'bg-green-500/10 border-green-500/30'
                              }`}>
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex items-center gap-2">
                                    <Clock className={`h-4 w-4 ${
                                      isExpired ? 'text-red-500' : 
                                      isNotStarted ? 'text-blue-500' :
                                      daysRemaining && daysRemaining <= 7 ? 'text-amber-500' : 
                                      'text-green-500'
                                    }`} />
                                    <span className="text-sm font-medium">
                                      {campaign.start_at ? new Date(campaign.start_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : 'No start'}
                                      {' → '}
                                      {campaign.end_at ? new Date(campaign.end_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Ongoing'}
                                    </span>
                                  </div>
                                  {endDate && (
                                    <Badge className={`text-xs ml-2 ${
                                      isExpired ? 'bg-red-500/20 text-red-600 border-red-500/30' :
                                      isNotStarted ? 'bg-blue-500/20 text-blue-600 border-blue-500/30' :
                                      daysRemaining <= 7 ? 'bg-amber-500/20 text-amber-600 border-amber-500/30' :
                                      'bg-green-500/20 text-green-600 border-green-500/30'
                                    }`}>
                                      {isExpired ? 'Expired' : 
                                       isNotStarted ? `Starts in ${Math.ceil((startDate - now) / (1000 * 60 * 60 * 24))} days` :
                                       `${daysRemaining} days left`}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {campaign.status === "PAYMENT_FAILED" && (
                              <DropdownMenuItem onClick={() => handleRetryPayment(campaign)}>
                                <CreditCard className="mr-2 h-4 w-4" />
                                Retry Payment
                              </DropdownMenuItem>
                            )}
                            {campaign.status === "ACTIVE" && (
                              campaignTier === "GOLD" ? (
                                <DropdownMenuItem onClick={() => handlePause(campaign.id)}>
                                  <Pause className="mr-2 h-4 w-4" />
                                  Pause Campaign
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem disabled className="text-muted-foreground cursor-not-allowed">
                                  <Pause className="mr-2 h-4 w-4 opacity-50" />
                                  Pause (Gold Tier Only)
                                </DropdownMenuItem>
                              )
                            )}
                            {campaign.status === "PAUSED" && (
                              <DropdownMenuItem onClick={() => handleActivate(campaign.id)}>
                                <Play className="mr-2 h-4 w-4" />
                                Resume Campaign
                              </DropdownMenuItem>
                            )}
                            {campaign.status === "DRAFT" && (
                              <DropdownMenuItem onClick={() => handleActivate(campaign.id)}>
                                <Play className="mr-2 h-4 w-4" />
                                Activate Campaign
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => openArchiveModal(campaign)}
                              className="text-amber-600 focus:text-amber-600 cursor-pointer"
                            >
                              <Archive className="mr-2 h-4 w-4" />
                              Archive
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Creatives Section */}
                      <div className="mb-4">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">Creatives</p>
                        <div className="grid grid-cols-2 gap-3">
                          {/* Banner */}
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">Banner</p>
                            {campaignCreatives.length > 0 ? (
                              <div className="relative h-24 rounded-lg overflow-hidden bg-secondary border border-border">
                                <img 
                                  src={campaignCreatives[0]?.file_url} 
                                  alt="Banner"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="h-24 rounded-lg bg-secondary/30 border border-dashed border-border flex items-center justify-center">
                                <span className="text-xs text-muted-foreground">No banner</span>
                              </div>
                            )}
                          </div>
                          {/* Panel */}
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">Panel</p>
                            {campaignCreatives.length > 1 ? (
                              <div className="relative h-24 rounded-lg overflow-hidden bg-secondary border border-border">
                                <img 
                                  src={campaignCreatives[1]?.file_url || campaignCreatives[0]?.file_url} 
                                  alt="Panel"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : campaignCreatives.length > 0 ? (
                              <div className="relative h-24 rounded-lg overflow-hidden bg-secondary border border-border">
                                <img 
                                  src={campaignCreatives[0]?.file_url} 
                                  alt="Panel"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="h-24 rounded-lg bg-secondary/30 border border-dashed border-border flex items-center justify-center">
                                <span className="text-xs text-muted-foreground">No panel</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Targeting Configuration */}
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">Targeting Configuration</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="p-2 rounded-lg bg-secondary/20">
                            <p className="text-xs text-muted-foreground">Location</p>
                            <p className="text-sm font-medium">
                              {(() => {
                                const parts = [];
                                if (targeting.location_ids?.length) parts.push(`${targeting.location_ids.length} venue${targeting.location_ids.length > 1 ? 's' : ''}`);
                                if (targeting.states?.length) parts.push(`${targeting.states.length} state${targeting.states.length > 1 ? 's' : ''}`);
                                if (targeting.postcodes?.length) parts.push(`${targeting.postcodes.length} postcode${targeting.postcodes.length > 1 ? 's' : ''}`);
                                if (targeting.suburbs?.length) parts.push(`${targeting.suburbs.length} suburb${targeting.suburbs.length > 1 ? 's' : ''}`);
                                return parts.length > 0 ? parts.join(', ') : 'All areas';
                              })()}
                            </p>
                          </div>
                          <div className="p-2 rounded-lg bg-secondary/20">
                            <p className="text-xs text-muted-foreground">Days</p>
                            <p className="text-sm font-medium">
                              {targeting.days?.length === 7 
                                ? "All days" 
                                : targeting.days?.length 
                                  ? `${targeting.days.length} days` 
                                  : "All days"}
                            </p>
                          </div>
                          <div className="p-2 rounded-lg bg-secondary/20">
                            <p className="text-xs text-muted-foreground">Time</p>
                            <p className="text-sm font-medium">
                              {targeting.time_slots?.length === 4 
                                ? "All day" 
                                : targeting.time_slots?.length 
                                  ? targeting.time_slots.map(t => t.charAt(0).toUpperCase() + t.slice(1, 3)).join(", ")
                                  : "All day"}
                            </p>
                          </div>
                          <div className="p-2 rounded-lg bg-secondary/20">
                            <p className="text-xs text-muted-foreground">Radius</p>
                            <p className="text-sm font-medium">
                              {targeting.radius_override ? `${targeting.radius_override}m` : "Default"}
                            </p>
                          </div>
                          <div className="p-2 rounded-lg bg-secondary/20">
                            <p className="text-xs text-muted-foreground">Gender</p>
                            <p className="text-sm font-medium capitalize">{targeting.gender || "All"}</p>
                          </div>
                          <div className="p-2 rounded-lg bg-secondary/20">
                            <p className="text-xs text-muted-foreground">Age Range</p>
                            <p className="text-sm font-medium">{targeting.age_range === "all" ? "All ages" : targeting.age_range || "All ages"}</p>
                          </div>
                          <div className="p-2 rounded-lg bg-secondary/20">
                            <p className="text-xs text-muted-foreground">Venue Type</p>
                            <p className="text-sm font-medium capitalize">{targeting.venue_type || "Any"}</p>
                          </div>
                          <div className="p-2 rounded-lg bg-secondary/20">
                            <p className="text-xs text-muted-foreground">Interests</p>
                            <p className="text-sm font-medium capitalize">{targeting.interests || "Any"}</p>
                          </div>
                        </div>
                      </div>

                      {/* Deal Section */}
                      {campaign.deal_available && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium flex items-center gap-2">
                            <Gift className="h-4 w-4 text-amber-500" />
                            Deal Available
                          </p>
                          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                            <p className="text-sm font-medium mb-1">{campaign.offer_text || "Special Offer"}</p>
                            <p className="text-xs text-muted-foreground">{campaign.deal_description || "Deal description"}</p>
                            {campaign.cta_text && (
                              <Badge className="mt-2 bg-amber-500/20 text-amber-600 border-amber-500/30">{campaign.cta_text}</Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Section - Pricing Metrics */}
                    <div className="w-56 shrink-0 border-l border-border pl-6">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3 font-medium">Enabled Pricing</p>
                      <div className="space-y-2">
                        {/* Views - Always shown */}
                        <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-blue-600">Views</span>
                            <span className="text-sm font-bold">${globalPricing?.cpm_rate?.toFixed(2) || "5.00"}/1k</span>
                          </div>
                        </div>

                        {/* Clicks */}
                        {pricing.cpc_enabled && (
                          <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/20">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-green-600">Clicks</span>
                              <span className="text-sm font-bold">${globalPricing?.cpc_rate?.toFixed(2) || "0.35"}/click</span>
                            </div>
                          </div>
                        )}

                        {/* Delivered Customers */}
                        {(pricing.cpdc_direct_enabled || 
                          pricing.cpdc_proximity_enabled || pricing.cpdc_prompted_enabled) && (
                          <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/20">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-purple-600">Delivered Customers</span>
                              <Badge variant="outline" className="text-xs bg-purple-500/10 border-purple-500/30">All Types</Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Direct</span>
                                <span className="font-medium">${globalPricing?.cpdc_direct_rate?.toFixed(2) || "8.00"}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Proximity</span>
                                <span className="font-medium">${globalPricing?.cpdc_proximity_rate?.toFixed(2) || "5.00"}</span>
                              </div>
                              <div className="flex justify-between col-span-2">
                                <span className="text-muted-foreground">Prompted</span>
                                <span className="font-medium">${globalPricing?.cpdc_prompted_rate?.toFixed(2) || "6.50"}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Show disabled state if no additional pricing enabled */}
                        {!pricing.cpc_enabled && !pricing.cpdc_direct_enabled && 
                         !pricing.cpdc_proximity_enabled && !pricing.cpdc_prompted_enabled && (
                          <p className="text-xs text-muted-foreground text-center py-2">Views only</p>
                        )}
                      </div>

                      {/* Campaign Budget */}
                      {campaign.budget_limit && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">Campaign Budget</p>
                          <div className="p-2 rounded-lg bg-pink-500/10 border border-pink-500/20">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-pink-600">Maximum Spend</span>
                              <span className="text-sm font-bold">${campaign.budget_limit.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}

        {/* New Campaign Creation Widget */}
        {isCreating ? (
          <Card className="bg-card border-primary border-2">
            <CardContent className="p-6">
              <div className="flex gap-6">
                {/* Left Section - Campaign Info & Creatives */}
                <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-blue-500/20 text-blue-600 border-blue-500/30">NEW</Badge>
                      </div>
                      <Input
                        value={newCampaign.name}
                        onChange={(e) => setNewCampaign(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Add Campaign Name"
                        className="text-xl font-bold border-0 border-b rounded-none px-0 focus-visible:ring-0"
                      />
                      <Input
                        value={newCampaign.description}
                        onChange={(e) => setNewCampaign(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Campaign description (optional)"
                        className="text-sm text-muted-foreground mt-2 border-0 border-b rounded-none px-0 focus-visible:ring-0"
                      />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={discardDraft}
                      title="Discard draft"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Draft indicator */}
                  {hasDraft && (
                    <div className="mb-4 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2">
                      <Save className="h-4 w-4 text-amber-500" />
                      <span className="text-xs text-amber-600">Draft auto-saved</span>
                    </div>
                  )}

                  {/* Creatives Section - Select from Library */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Creatives</p>
                      <p className="text-xs text-muted-foreground">Click to select from library</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Banner - 640x200 (2x for HiDPI) */}
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Banner (640×200)</p>
                        {newCampaign.selectedBanner ? (
                          <div 
                            className="relative rounded-lg overflow-hidden bg-secondary border border-primary cursor-pointer group"
                            style={{ aspectRatio: '320/100' }}
                            onClick={() => openCreativePicker('BANNER')}
                          >
                            <img src={newCampaign.selectedBanner.file_url} alt="Banner" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Library className="h-6 w-6 text-white" />
                            </div>
                            <div className="absolute top-1 right-1">
                              <Check className="h-4 w-4 text-green-500 bg-white rounded-full p-0.5" />
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="rounded-lg bg-secondary/30 border-2 border-dashed border-border hover:border-primary/50 transition-colors cursor-pointer flex flex-col items-center justify-center"
                            style={{ aspectRatio: '320/100' }}
                            onClick={() => openCreativePicker('BANNER')}
                            data-testid="select-banner-btn"
                          >
                            <Library className="h-6 w-6 text-muted-foreground mb-1" />
                            <span className="text-xs text-muted-foreground">Select from Library</span>
                          </div>
                        )}
                      </div>
                      {/* Panel - 16:9 Widescreen ratio */}
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Panel (16:9)</p>
                        {newCampaign.selectedPanel ? (
                          <div 
                            className="relative rounded-lg overflow-hidden bg-secondary border border-primary cursor-pointer group"
                            style={{ aspectRatio: '16/9' }}
                            onClick={() => openCreativePicker('PANEL')}
                          >
                            <img src={newCampaign.selectedPanel.file_url} alt="Panel" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Library className="h-6 w-6 text-white" />
                            </div>
                            <div className="absolute top-1 right-1">
                              <Check className="h-4 w-4 text-green-500 bg-white rounded-full p-0.5" />
                            </div>
                          </div>
                        ) : (
                          <div 
                            className="rounded-lg bg-secondary/30 border-2 border-dashed border-border hover:border-primary/50 transition-colors cursor-pointer flex flex-col items-center justify-center"
                            style={{ aspectRatio: '16/9' }}
                            onClick={() => openCreativePicker('PANEL')}
                            data-testid="select-panel-btn"
                          >
                            <Library className="h-6 w-6 text-muted-foreground mb-1" />
                            <span className="text-xs text-muted-foreground">Select from Library</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Campaign Tier Selection - Horizontal Layout with Checkboxes */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Campaign Tier <span className="text-red-500">*</span></p>
                        <p className="text-xs text-muted-foreground mt-1">Select your campaign tier — subscription paid at start of campaign</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs text-muted-foreground hover:text-foreground gap-1 h-7 px-2"
                        onClick={() => setTiersExpanded(prev => !prev)}
                      >
                        {tiersExpanded ? 'Hide Details' : 'View Details'}
                        {tiersExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {Object.entries(CAMPAIGN_TIER_CONFIG).map(([tierKey, config]) => {
                        const isSelected = newCampaign.tier === tierKey;
                        // `enabled` is admin-controlled in /admin/tier-pricing. When false,
                        // the card stays visible (so prospects see what's coming) but is
                        // greyed out, can't be clicked, and shows a "Coming Soon" badge.
                        const isDisabled = config.enabled === false;
                        
                        const tierColors = {
                          GOLD: { border: 'border-amber-500', bg: 'bg-amber-500/10', text: 'text-amber-600', icon: '👑', checkAccent: 'data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500' },
                          SILVER: { border: 'border-slate-400', bg: 'bg-slate-400/10', text: 'text-slate-600', icon: '🥈', checkAccent: 'data-[state=checked]:bg-slate-500 data-[state=checked]:border-slate-500' },
                          BRONZE: { border: 'border-orange-700', bg: 'bg-orange-700/10', text: 'text-orange-700', icon: '🥉', checkAccent: 'data-[state=checked]:bg-orange-700 data-[state=checked]:border-orange-700' }
                        };
                        const colors = tierColors[tierKey];
                        
                        // Calculate price based on currently selected duration
                        // Matches backend: tier_cost = price_monthly × duration_months
                        const DURATION_MONTHS = { "1_month": 1, "3_months": 3, "6_months": 6, "12_months": 12 };
                        const tierPrice = newCampaign.duration
                          ? (config.price_monthly || 0) * (DURATION_MONTHS[newCampaign.duration] || 1)
                          : 0;

                        const handleSelectTier = () => {
                          if (isDisabled) return;
                          const tierFeatures = config.features;
                          setNewCampaign(prev => {
                            const needsDurationReset = tierKey === 'BRONZE' && prev.duration !== '12_months';
                            return {
                              ...prev,
                              tier: tierKey,
                              duration: needsDurationReset ? '12_months' : prev.duration,
                              pricing: {
                                cpm_enabled: true,
                                cpc_enabled: tierFeatures.cpc_enabled ? prev.pricing.cpc_enabled : false,
                                cpdc_direct_enabled: tierFeatures.cpdc_enabled ? prev.pricing.cpdc_direct_enabled : false,
                                cpdc_proximity_enabled: tierFeatures.cpdc_enabled ? prev.pricing.cpdc_proximity_enabled : false,
                                cpdc_prompted_enabled: tierFeatures.cpdc_enabled ? prev.pricing.cpdc_prompted_enabled : false,
                              },
                              deal_available: tierFeatures.deals_enabled ? prev.deal_available : false,
                              targeting: tierFeatures.basic_targeting ? {
                                ...prev.targeting,
                                venue_types: tierFeatures.advanced_targeting ? prev.targeting.venue_types : ["any"],
                                interests: tierFeatures.advanced_targeting ? prev.targeting.interests : ["any"],
                              } : {
                                ...prev.targeting,
                                enabled: false,
                                location_ids: [],
                                states: [],
                                postcodes: [],
                                suburbs: [],
                                days: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
                                time_slots: ["morning", "afternoon", "evening", "night"],
                                gender: "all",
                                age_ranges: ["all"],
                                venue_types: ["any"],
                                interests: ["any"],
                              }
                            };
                          });
                        };

                        return (
                          <div
                            key={tierKey}
                            className={`rounded-lg border-2 transition-all overflow-hidden ${
                              isDisabled
                                ? 'border-border bg-muted/30 opacity-60 cursor-not-allowed'
                                : isSelected
                                  ? `${colors.border} ${colors.bg} cursor-pointer`
                                  : 'border-border hover:border-muted-foreground/40 cursor-pointer'
                            }`}
                            data-testid={`tier-${tierKey.toLowerCase()}`}
                            onClick={handleSelectTier}
                            title={isDisabled ? `${config.name} tier is coming soon` : undefined}
                          >
                            {/* Tier Header - Always visible */}
                            <div className={`p-3 ${isSelected && !isDisabled ? colors.bg : ''}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-2.5 min-w-0">
                                  <Checkbox
                                    checked={isSelected && !isDisabled}
                                    onCheckedChange={handleSelectTier}
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={isDisabled}
                                    className={`mt-0.5 ${colors.checkAccent}`}
                                    data-testid={`select-tier-${tierKey.toLowerCase()}`}
                                  />
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="text-base leading-none">{colors.icon}</span>
                                      <span className={`font-bold text-sm ${colors.text}`}>{config.name}</span>
                                      {isDisabled && (
                                        <Badge className="bg-blue-500/20 text-blue-600 border-blue-500/30 text-[9px] px-1.5 py-0">
                                          Coming Soon
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{config.tagline}</p>
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  {config.is_free ? (
                                    <Badge className="bg-green-500/20 text-green-600 border-green-500/30 text-[10px] px-1.5 py-0">Free</Badge>
                                  ) : (
                                    <span className={`font-bold text-sm tabular-nums ${tierPrice > 0 && !isDisabled ? colors.text : 'text-muted-foreground'}`}>
                                      ${tierPrice.toLocaleString()}
                                    </span>
                                  )}
                                  {!config.is_free && !isDisabled && (
                                    <p className="text-[9px] text-muted-foreground mt-0.5">
                                      {newCampaign.duration ? '' : 'select duration'}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Expanded Feature Details (single toggle for all) */}
                            {tiersExpanded && (
                              <div className="px-3 pb-3 pt-2 border-t border-border/50" onClick={(e) => e.stopPropagation()}>
                                <p className="text-xs text-muted-foreground mb-2">{config.description}</p>
                                <div className="space-y-0.5">
                                  {config.features.cpm_enabled && <p className="text-[11px] text-green-600">✓ Views</p>}
                                  {config.features.cpc_enabled ? <p className="text-[11px] text-green-600">✓ Clicks</p> : <p className="text-[11px] text-muted-foreground/60">✗ Clicks</p>}
                                  {config.features.cpdc_enabled ? <p className="text-[11px] text-green-600">✓ Delivered Customers</p> : <p className="text-[11px] text-muted-foreground/60">✗ Delivered Customers</p>}
                                  {config.features.deals_enabled ? <p className="text-[11px] text-green-600">✓ Deals & Offers</p> : <p className="text-[11px] text-muted-foreground/60">✗ Deals & Offers</p>}
                                  {config.features.basic_targeting ? <p className="text-[11px] text-green-600">✓ Basic Targeting</p> : <p className="text-[11px] text-muted-foreground/60">✗ Basic Targeting</p>}
                                  {config.features.advanced_targeting ? <p className="text-[11px] text-green-600">✓ Advanced Targeting</p> : <p className="text-[11px] text-muted-foreground/60">✗ Advanced Targeting</p>}
                                  {config.features.animated_creatives ? <p className="text-[11px] text-green-600">✓ Animated Creatives</p> : <p className="text-[11px] text-muted-foreground/60">✗ Animated Creatives</p>}
                                  {config.features.pause_enabled ? <p className="text-[11px] text-green-600">✓ Campaign Pause</p> : <p className="text-[11px] text-muted-foreground/60">✗ Campaign Pause</p>}
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-2">
                                  Max {config.max_campaigns_per_account} campaign(s) per account
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Budget & Schedule */}
                  <div className="mb-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">Duration & Budget</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Campaign Duration <span className="text-red-500">*</span></Label>
                        {newCampaign.tier === 'BRONZE' ? (
                          <div className="h-9 px-3 flex items-center rounded-md border border-border bg-muted/50 text-sm text-muted-foreground">
                            12 Months
                          </div>
                        ) : (
                        <Select
                          value={newCampaign.duration || ""}
                          onValueChange={(val) => {
                            // Auto-calculate end date based on duration
                            const durationDays = {
                              "1_month": 30,
                              "3_months": 90,
                              "6_months": 180,
                              "12_months": 365
                            };

                            let endDate = "";
                            if (val && newCampaign.start_at) {
                              const start = new Date(newCampaign.start_at);
                              const end = new Date(start);
                              end.setDate(end.getDate() + durationDays[val]);
                              endDate = end.toISOString().split('T')[0];
                            }

                            setNewCampaign(prev => ({
                              ...prev,
                              duration: val,
                              end_at: endDate || prev.end_at
                            }));
                          }}
                        >
                          <SelectTrigger className="h-9" data-testid="duration-select">
                            <SelectValue placeholder="Select duration" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1_month">1 Month</SelectItem>
                            <SelectItem value="3_months">3 Months</SelectItem>
                            <SelectItem value="6_months">6 Months</SelectItem>
                            <SelectItem value="12_months">12 Months</SelectItem>
                          </SelectContent>
                        </Select>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Start Date <span className="text-red-500">*</span></Label>
                        <Input
                          type="date"
                          value={newCampaign.start_at}
                          min={getTodayDate()}
                          onChange={(e) => {
                            const startDate = e.target.value;
                            // Recalculate end date if duration is set
                            let endDate = newCampaign.end_at;
                            if (newCampaign.duration && startDate) {
                              const durationDays = {
                                "1_month": 30,
                                "3_months": 90,
                                "6_months": 180,
                                "12_months": 365
                              };
                              const start = new Date(startDate);
                              const end = new Date(start);
                              end.setDate(end.getDate() + durationDays[newCampaign.duration]);
                              endDate = end.toISOString().split('T')[0];
                            }
                            setNewCampaign(prev => ({ ...prev, start_at: startDate, end_at: endDate }));
                          }}
                          className="h-9"
                          required
                          data-testid="start-date-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">End Date (Auto)</Label>
                        <Input
                          type="date"
                          value={newCampaign.end_at}
                          min={newCampaign.start_at || getTodayDate()}
                          className="h-9 bg-secondary/30"
                          disabled={!!newCampaign.duration}
                          onChange={(e) => setNewCampaign(prev => ({ ...prev, end_at: e.target.value }))}
                          data-testid="end-date-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Campaign Budget ($)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="No limit"
                          value={newCampaign.budget_limit}
                          onChange={(e) => setNewCampaign(prev => ({ ...prev, budget_limit: e.target.value }))}
                          className="h-9"
                          data-testid="budget-input"
                        />
                      </div>
                    </div>
                    {newCampaign.tier !== 'BRONZE' && (
                      <p className="text-xs text-muted-foreground mt-2">
                        When budget is reached, campaign defaults to Views only which are unbillable.
                      </p>
                    )}
                  </div>

                  {/* Targeting Configuration Dropdowns */}
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">Targeting Configuration</p>
                    {(() => {
                      const selectedTierConfig = CAMPAIGN_TIER_CONFIG[newCampaign.tier] || CAMPAIGN_TIER_CONFIG.BRONZE;
                      const canUseBasicTargeting = selectedTierConfig.features.basic_targeting;  // Silver+
                      const canUseAdvancedTargeting = selectedTierConfig.features.advanced_targeting;  // Gold only
                      const canUseCpdc = selectedTierConfig.features.cpdc_enabled;
                      
                      return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Location</Label>
                        <Button
                          variant="outline"
                          className={`w-full h-10 justify-between text-left font-normal ${!canUseBasicTargeting ? "opacity-50" : ""}`}
                          onClick={() => canUseBasicTargeting && setLocationTargetingOpen(true)}
                          disabled={!canUseBasicTargeting}
                        >
                          <span className="truncate text-sm">
                            {!canUseBasicTargeting ? "All Areas" : getLocationTargetingSummary()}
                          </span>
                          <MapPin className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                        </Button>
                        {!canUseBasicTargeting && <p className="text-xs text-gray-400">Silver & Gold only</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Days</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full h-10 justify-between text-left font-normal ${!canUseBasicTargeting ? "opacity-50" : ""}`}
                              disabled={!canUseBasicTargeting}
                            >
                              <span className="truncate text-sm">
                                {!canUseBasicTargeting ? "All Days" :
                                  newCampaign.targeting.days.length === 7 ? "All Days" :
                                  newCampaign.targeting.days.length === 0 ? "Select days" :
                                  `${newCampaign.targeting.days.length} day${newCampaign.targeting.days.length > 1 ? 's' : ''} selected`}
                              </span>
                              <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-3" align="start">
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2 pb-2 border-b">
                                <Checkbox
                                  id="days-all"
                                  checked={newCampaign.targeting.days.length === 7}
                                  onCheckedChange={(checked) => {
                                    setNewCampaign(prev => ({
                                      ...prev,
                                      targeting: {
                                        ...prev.targeting,
                                        days: checked 
                                          ? ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
                                          : []
                                      }
                                    }));
                                  }}
                                />
                                <label htmlFor="days-all" className="text-sm font-medium cursor-pointer">All Days</label>
                              </div>
                              {daysOfWeek.map((day) => (
                                <div key={day.value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`day-${day.value}`}
                                    checked={newCampaign.targeting.days.includes(day.value)}
                                    onCheckedChange={(checked) => {
                                      setNewCampaign(prev => ({
                                        ...prev,
                                        targeting: {
                                          ...prev.targeting,
                                          days: checked
                                            ? [...prev.targeting.days, day.value]
                                            : prev.targeting.days.filter(d => d !== day.value)
                                        }
                                      }));
                                    }}
                                  />
                                  <label htmlFor={`day-${day.value}`} className="text-sm cursor-pointer">{day.label}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {!canUseBasicTargeting && <p className="text-xs text-gray-400">Silver & Gold only</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Time</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full h-10 justify-between text-left font-normal ${!canUseBasicTargeting ? "opacity-50" : ""}`}
                              disabled={!canUseBasicTargeting}
                            >
                              <span className="truncate text-sm">
                                {!canUseBasicTargeting ? "All Day" :
                                  newCampaign.targeting.time_slots.length === 4 ? "All Day" :
                                  newCampaign.targeting.time_slots.length === 0 ? "Select times" :
                                  `${newCampaign.targeting.time_slots.length} slot${newCampaign.targeting.time_slots.length > 1 ? 's' : ''} selected`}
                              </span>
                              <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-3" align="start">
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2 pb-2 border-b">
                                <Checkbox
                                  id="time-all"
                                  checked={newCampaign.targeting.time_slots.length === 4}
                                  onCheckedChange={(checked) => {
                                    setNewCampaign(prev => ({
                                      ...prev,
                                      targeting: {
                                        ...prev.targeting,
                                        time_slots: checked 
                                          ? ["morning", "afternoon", "evening", "night"]
                                          : []
                                      }
                                    }));
                                  }}
                                />
                                <label htmlFor="time-all" className="text-sm font-medium cursor-pointer">All Day</label>
                              </div>
                              {timeSlots.map((slot) => (
                                <div key={slot.value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`time-${slot.value}`}
                                    checked={newCampaign.targeting.time_slots.includes(slot.value)}
                                    onCheckedChange={(checked) => {
                                      setNewCampaign(prev => ({
                                        ...prev,
                                        targeting: {
                                          ...prev.targeting,
                                          time_slots: checked
                                            ? [...prev.targeting.time_slots, slot.value]
                                            : prev.targeting.time_slots.filter(t => t !== slot.value)
                                        }
                                      }));
                                    }}
                                  />
                                  <label htmlFor={`time-${slot.value}`} className="text-sm cursor-pointer">{slot.label}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {!canUseBasicTargeting && <p className="text-xs text-gray-400">Silver & Gold only</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Gender</Label>
                        <Select 
                          value={newCampaign.targeting.gender}
                          onValueChange={(val) => setNewCampaign(prev => ({
                            ...prev,
                            targeting: { ...prev.targeting, gender: val }
                          }))}
                          disabled={!canUseBasicTargeting}
                        >
                          <SelectTrigger className={`h-10 ${!canUseBasicTargeting ? "opacity-50" : ""}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                          </SelectContent>
                        </Select>
                        {!canUseBasicTargeting && <p className="text-xs text-gray-400">Silver & Gold only</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Age Range</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full h-10 justify-between text-left font-normal ${!canUseBasicTargeting ? "opacity-50" : ""}`}
                              disabled={!canUseBasicTargeting}
                            >
                              <span className="truncate text-sm">
                                {!canUseBasicTargeting ? "All Ages" :
                                  newCampaign.targeting.age_ranges.includes("all") ? "All Ages" :
                                  newCampaign.targeting.age_ranges.length === 0 ? "Select ages" :
                                  `${newCampaign.targeting.age_ranges.length} range${newCampaign.targeting.age_ranges.length > 1 ? 's' : ''} selected`}
                              </span>
                              <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-3" align="start">
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2 pb-2 border-b">
                                <Checkbox
                                  id="age-all"
                                  checked={newCampaign.targeting.age_ranges.includes("all")}
                                  onCheckedChange={(checked) => {
                                    setNewCampaign(prev => ({
                                      ...prev,
                                      targeting: {
                                        ...prev.targeting,
                                        age_ranges: checked ? ["all"] : []
                                      }
                                    }));
                                  }}
                                />
                                <label htmlFor="age-all" className="text-sm font-medium cursor-pointer">All Ages</label>
                              </div>
                              {ageRanges.map((range) => (
                                <div key={range.value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`age-${range.value}`}
                                    checked={newCampaign.targeting.age_ranges.includes(range.value)}
                                    onCheckedChange={(checked) => {
                                      setNewCampaign(prev => {
                                        let newRanges = prev.targeting.age_ranges.filter(r => r !== "all");
                                        if (checked) {
                                          newRanges = [...newRanges, range.value];
                                        } else {
                                          newRanges = newRanges.filter(r => r !== range.value);
                                        }
                                        return {
                                          ...prev,
                                          targeting: {
                                            ...prev.targeting,
                                            age_ranges: newRanges.length === 0 ? ["all"] : newRanges
                                          }
                                        };
                                      });
                                    }}
                                  />
                                  <label htmlFor={`age-${range.value}`} className="text-sm cursor-pointer">{range.label}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {!canUseBasicTargeting && <p className="text-xs text-gray-400">Silver & Gold only</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Venue Type</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full h-10 justify-between text-left font-normal ${!canUseAdvancedTargeting ? "opacity-50" : ""}`}
                              disabled={!canUseAdvancedTargeting}
                            >
                              <span className="truncate text-sm">
                                {!canUseAdvancedTargeting ? "Any" :
                                  newCampaign.targeting.venue_types.includes("any") ? "Any" :
                                  newCampaign.targeting.venue_types.length === 0 ? "Select venues" :
                                  `${newCampaign.targeting.venue_types.length} type${newCampaign.targeting.venue_types.length > 1 ? 's' : ''} selected`}
                              </span>
                              <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-3" align="start">
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              <div className="flex items-center space-x-2 pb-2 border-b">
                                <Checkbox
                                  id="venue-any"
                                  checked={newCampaign.targeting.venue_types.includes("any")}
                                  onCheckedChange={(checked) => {
                                    setNewCampaign(prev => ({
                                      ...prev,
                                      targeting: {
                                        ...prev.targeting,
                                        venue_types: checked ? ["any"] : []
                                      }
                                    }));
                                  }}
                                />
                                <label htmlFor="venue-any" className="text-sm font-medium cursor-pointer">Any</label>
                              </div>
                              {venueTypes.map((venue) => (
                                <div key={venue.value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`venue-${venue.value}`}
                                    checked={newCampaign.targeting.venue_types.includes(venue.value)}
                                    onCheckedChange={(checked) => {
                                      setNewCampaign(prev => {
                                        let newTypes = prev.targeting.venue_types.filter(v => v !== "any");
                                        if (checked) {
                                          newTypes = [...newTypes, venue.value];
                                        } else {
                                          newTypes = newTypes.filter(v => v !== venue.value);
                                        }
                                        return {
                                          ...prev,
                                          targeting: {
                                            ...prev.targeting,
                                            venue_types: newTypes.length === 0 ? ["any"] : newTypes
                                          }
                                        };
                                      });
                                    }}
                                  />
                                  <label htmlFor={`venue-${venue.value}`} className="text-sm cursor-pointer">{venue.label}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {!canUseAdvancedTargeting && <p className="text-xs text-yellow-500">Gold only</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Interests</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full h-10 justify-between text-left font-normal ${!canUseAdvancedTargeting ? "opacity-50" : ""}`}
                              disabled={!canUseAdvancedTargeting}
                            >
                              <span className="truncate text-sm">
                                {!canUseAdvancedTargeting ? "Any" :
                                  newCampaign.targeting.interests.includes("any") ? "Any" :
                                  newCampaign.targeting.interests.length === 0 ? "Select interests" :
                                  `${newCampaign.targeting.interests.length} interest${newCampaign.targeting.interests.length > 1 ? 's' : ''} selected`}
                              </span>
                              <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-3" align="start">
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2 pb-2 border-b">
                                <Checkbox
                                  id="interest-any"
                                  checked={newCampaign.targeting.interests.includes("any")}
                                  onCheckedChange={(checked) => {
                                    setNewCampaign(prev => ({
                                      ...prev,
                                      targeting: {
                                        ...prev.targeting,
                                        interests: checked ? ["any"] : []
                                      }
                                    }));
                                  }}
                                />
                                <label htmlFor="interest-any" className="text-sm font-medium cursor-pointer">Any</label>
                              </div>
                              {interestOptions.map((interest) => (
                                <div key={interest.value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`interest-${interest.value}`}
                                    checked={newCampaign.targeting.interests.includes(interest.value)}
                                    onCheckedChange={(checked) => {
                                      setNewCampaign(prev => {
                                        let newInterests = prev.targeting.interests.filter(i => i !== "any");
                                        if (checked) {
                                          newInterests = [...newInterests, interest.value];
                                        } else {
                                          newInterests = newInterests.filter(i => i !== interest.value);
                                        }
                                        return {
                                          ...prev,
                                          targeting: {
                                            ...prev.targeting,
                                            interests: newInterests.length === 0 ? ["any"] : newInterests
                                          }
                                        };
                                      });
                                    }}
                                  />
                                  <label htmlFor={`interest-${interest.value}`} className="text-sm cursor-pointer">{interest.label}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {!canUseAdvancedTargeting && <p className="text-xs text-yellow-500">Gold only</p>}
                      </div>
                    </div>
                      );
                    })()}
                  </div>

                  {/* Deal Settings */}
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Deal Settings</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Passengers can save or redeem deals in-app</p>
                      </div>
                      <Switch
                        checked={newCampaign.deal_available}
                        onCheckedChange={(checked) => setNewCampaign(prev => ({
                          ...prev,
                          deal_available: checked
                        }))}
                        disabled={!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.deals_enabled}
                        data-testid="deal-toggle"
                      />
                    </div>
                    {!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.deals_enabled && (
                      <p className="text-xs text-gray-400 mb-3">Silver or Gold only</p>
                    )}
                    
                    {newCampaign.deal_available && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Deal Form */}
                        <div className="space-y-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                          {/* AI Suggestion Button */}
                          <div className="flex justify-end">
                            <Popover open={suggestionPopoverOpen} onOpenChange={setSuggestionPopoverOpen}>
                              <PopoverTrigger asChild>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={suggestDeal}
                                  disabled={suggestingDeal}
                                  className="text-xs"
                                  data-testid="ai-suggest-deal-btn"
                                >
                                  {suggestingDeal ? (
                                    <><Loader2 className="mr-1 h-3 w-3 animate-spin" />Generating...</>
                                  ) : (
                                    <><Sparkles className="mr-1 h-3 w-3" />AI Suggest Deal</>
                                  )}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80 p-0" align="end">
                                <div className="p-3 border-b border-border">
                                  <p className="font-medium text-sm">Deal Suggestions</p>
                                  <p className="text-xs text-muted-foreground">Click to apply, then customize</p>
                                </div>
                                <div className="max-h-64 overflow-y-auto">
                                  {dealSuggestions.map((suggestion, i) => (
                                    <div 
                                      key={i}
                                      className="p-3 border-b border-border last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
                                      onClick={() => applyDealSuggestion(suggestion)}
                                    >
                                      <p className="font-medium text-sm text-amber-500">{suggestion.offer_text}</p>
                                      <p className="text-xs text-muted-foreground mt-1">{suggestion.deal_description}</p>
                                      <Badge variant="outline" className="mt-2 text-xs">{suggestion.cta_text}</Badge>
                                    </div>
                                  ))}
                                  {dealSuggestions.length === 0 && !suggestingDeal && (
                                    <div className="p-4 text-center text-sm text-muted-foreground">
                                      Click "AI Suggest Deal" to generate suggestions
                                    </div>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                          
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Deal Title</Label>
                            <p className="text-xs italic text-muted-foreground/70 mb-1">Short headline shown on deal card</p>
                            <Input
                              value={newCampaign.offer_text}
                              onChange={(e) => setNewCampaign(prev => ({ ...prev, offer_text: e.target.value }))}
                              placeholder="e.g., 20% Off First Coffee"
                              className="h-9 text-sm"
                              data-testid="deal-title-input"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Deal Description</Label>
                            <p className="text-xs italic text-muted-foreground/70 mb-1">Detailed offer terms and conditions</p>
                            <Input
                              value={newCampaign.deal_description}
                              onChange={(e) => setNewCampaign(prev => ({ ...prev, deal_description: e.target.value }))}
                              placeholder="e.g., Valid for new customers only. Show this deal at checkout."
                              className="h-9 text-sm"
                              data-testid="deal-description-input"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">CTA Button Text</Label>
                            <p className="text-xs italic text-muted-foreground/70 mb-1">Action button text shown to passengers</p>
                            <Input
                              value={newCampaign.cta_text}
                              onChange={(e) => setNewCampaign(prev => ({ ...prev, cta_text: e.target.value }))}
                              placeholder="e.g., Claim Deal"
                              className="h-9 text-sm"
                              data-testid="cta-text-input"
                            />
                          </div>
                          <p className="text-xs text-amber-600 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Deals expire 24 hours after being saved
                          </p>
                        </div>
                        
                        {/* Deal Preview */}
                        <div className="p-3 rounded-lg bg-secondary/30 border border-border">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-3">Deal Preview</p>
                          <div className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-700">
                            {/* Creative image or placeholder */}
                            <div className="h-24 bg-gray-800 relative flex items-center justify-center">
                              {newCampaign.selectedPanel?.file_url ? (
                                <img
                                  src={newCampaign.selectedPanel.file_url}
                                  alt="Ad creative"
                                  className="w-full h-full object-cover"
                                />
                              ) : newCampaign.selectedBanner?.file_url ? (
                                <img
                                  src={newCampaign.selectedBanner.file_url}
                                  alt="Ad creative"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <ImagePlus className="h-8 w-8 text-gray-600" />
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
                            </div>

                            {/* Deal content */}
                            <div className="p-4 space-y-3">
                              <h4 className="text-base font-bold text-white">
                                {newCampaign.name || "Your Business"}
                              </h4>

                              <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-3">
                                <p className="text-amber-400 font-semibold text-sm">
                                  {newCampaign.offer_text || "Deal title appears here"}
                                </p>
                                {(newCampaign.deal_description) && (
                                  <p className="text-amber-300/70 text-xs mt-1">
                                    {newCampaign.deal_description}
                                  </p>
                                )}
                              </div>

                              <button
                                disabled
                                className="w-full bg-amber-500 text-black font-bold py-2.5 px-4 rounded-xl text-sm flex items-center justify-center gap-1.5"
                              >
                                {newCampaign.cta_text || "Save to My Deals"}
                              </button>

                              <p className="text-gray-500 text-xs text-center">
                                Customers will be able to redeem these offers via a QR code in-store
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sponsored Trip Add-on — Gold-only placeholder. Mechanics
                      (separate vs shared budget, allocation rules, Stripe
                      handling) are still in design; this is a stub so the
                      slot is visible to advertisers and stakeholders. */}
                  {(() => {
                    const goldOnly = CAMPAIGN_TIER_CONFIG.GOLD?.features?.pause_enabled;
                    const tierSupports = CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.pause_enabled;
                    if (!goldOnly) return null;  // defensive — Gold flag missing entirely
                    return (
                      <div className={`mt-4 pt-4 border-t border-border ${!tierSupports ? 'opacity-60' : ''}`}>
                        <div className="flex items-start justify-between mb-2 gap-3">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Sponsored Trip Add-on</p>
                              <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30 text-[9px] px-1.5 py-0">Gold</Badge>
                              <Badge className="bg-blue-500/20 text-blue-600 border-blue-500/30 text-[9px] px-1.5 py-0">Coming Soon</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 max-w-prose">
                              Allocate part of your campaign budget to fully or partially sponsor passenger trips that match your brand. Your name appears within the trip and/or at the trip's conclusion.
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled
                            className="shrink-0 cursor-not-allowed"
                            title="Available soon — pricing model in design"
                            data-testid="sponsored-trip-configure-btn"
                          >
                            Configure (coming soon)
                          </Button>
                        </div>
                        {!tierSupports && (
                          <p className="text-xs text-amber-600/70 mt-1">Gold tier only — upgrade to enable.</p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Right Section - Pricing (Read-only rates from admin settings) */}
                <div className="w-56 shrink-0 border-l border-border pl-6">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-4 font-medium">Campaign Metrics</p>
                  
                  <div className="space-y-3">
                    {/* Views - Always enabled */}
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-blue-600">Views</span>
                        <Badge variant="outline" className="text-xs bg-blue-500/10 border-blue-500/30">Default</Badge>
                      </div>
                      <p className="text-lg font-bold mt-1">${((globalPricing?.cpm_rate || 120) / 1000).toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">per view</p>
                    </div>

                    {/* Clicks */}
                    <div className={`p-3 rounded-lg ${newCampaign.pricing.cpc_enabled ? 'bg-green-500/10 border-green-500/20' : 'bg-secondary/30 border-border'} border ${!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpc_enabled ? 'opacity-50' : ''}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-medium ${newCampaign.pricing.cpc_enabled ? 'text-green-600' : 'text-muted-foreground'}`}>Clicks</span>
                        <Switch
                          checked={newCampaign.pricing.cpc_enabled}
                          onCheckedChange={(checked) => setNewCampaign(prev => ({
                            ...prev,
                            pricing: { ...prev.pricing, cpc_enabled: checked }
                          }))}
                          disabled={!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpc_enabled}
                          data-testid="cpc-toggle"
                        />
                      </div>
                      {!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpc_enabled && (
                        <p className="text-xs text-gray-400 mt-1">Silver or Gold only</p>
                      )}
                      {newCampaign.pricing.cpc_enabled && (
                        <>
                          <p className="text-lg font-bold mt-1">${globalPricing?.cpc_rate?.toFixed(2) || "0.35"}</p>
                          <p className="text-xs text-muted-foreground">per click</p>
                        </>
                      )}
                    </div>

                    {/* Delivered Customers */}
                    <div className={`p-3 rounded-lg ${newCampaign.pricing.cpdc_enabled ? 'bg-purple-500/10 border-purple-500/20' : 'bg-secondary/30 border-border'} border ${!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpdc_enabled ? 'opacity-50' : ''}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs font-medium flex items-center ${newCampaign.pricing.cpdc_enabled ? 'text-purple-600' : 'text-muted-foreground'}`}>Delivered Customers<CPDCInfoButton /></span>
                        <Switch
                          checked={newCampaign.pricing.cpdc_enabled}
                          onCheckedChange={(checked) => setNewCampaign(prev => ({
                            ...prev,
                            pricing: { 
                              ...prev.pricing, 
                              cpdc_enabled: checked,
                              cpdc_direct_enabled: checked,
                              cpdc_proximity_enabled: checked,
                              cpdc_prompted_enabled: checked
                            }
                          }))}
                          disabled={!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpdc_enabled}
                          data-testid="cpdc-toggle"
                        />
                      </div>
                      {!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpdc_enabled && (
                        <p className="text-xs text-gray-400 mt-1">Silver or Gold only</p>
                      )}
                      {/* Always show rates if tier supports CPDC, regardless of toggle state */}
                      {CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpdc_enabled && (
                        <div className={`mt-3 space-y-2 pt-2 border-t ${newCampaign.pricing.cpdc_enabled ? 'border-purple-500/20' : 'border-border'}`}>
                          <div className="grid grid-cols-1 gap-2">
                            <div className="text-xs flex justify-between">
                              <span className="text-muted-foreground">Direct</span>
                              <span className={`font-bold ${newCampaign.pricing.cpdc_enabled ? 'text-purple-600' : 'text-muted-foreground'}`}>${globalPricing?.cpdc_direct_rate?.toFixed(2) || "8.00"}</span>
                            </div>
                            <div className="text-xs flex justify-between">
                              <span className="text-muted-foreground">Proximity</span>
                              <span className={`font-bold ${newCampaign.pricing.cpdc_enabled ? 'text-purple-600' : 'text-muted-foreground'}`}>${globalPricing?.cpdc_proximity_rate?.toFixed(2) || "5.00"}</span>
                            </div>
                            <div className="text-xs flex justify-between">
                              <span className="text-muted-foreground">Prompted</span>
                              <span className={`font-bold ${newCampaign.pricing.cpdc_enabled ? 'text-purple-600' : 'text-muted-foreground'}`}>${globalPricing?.cpdc_prompted_rate?.toFixed(2) || "6.50"}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Save Button */}
                    <Button 
                      className="w-full mt-4" 
                      onClick={() => setConfirmSaveOpen(true)}
                      disabled={!newCampaign.name.trim()}
                    >
                      <Save className="mr-2 h-4 w-4" />
                      Save Campaign
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Add New Campaign Button */
          <Card 
            className="bg-card border-border border-dashed hover:border-primary/50 transition-all cursor-pointer group"
            onClick={() => setIsCreating(true)}
            data-testid="create-campaign-btn"
          >
            <CardContent className="flex items-center justify-center gap-4 p-8">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Plus className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-lg">New Campaign</p>
                <p className="text-sm text-muted-foreground">Create a new advertising campaign</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirm Save Dialog */}
      <Dialog open={confirmSaveOpen} onOpenChange={setConfirmSaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm Save
            </DialogTitle>
            <DialogDescription className="pt-2">
              Once saved, this campaign cannot be edited, only paused. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmSaveOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCampaign} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Yes, Save Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Creative Picker Dialog */}
      <Dialog open={creativePickerOpen} onOpenChange={setCreativePickerOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Library className="h-5 w-5" />
              Select {creativePickerType === 'BANNER' ? 'Banner' : 'Panel'}
            </DialogTitle>
            <DialogDescription>
              Choose a creative from your library. {creativePickerType === 'BANNER' ? 'Banners are 320×100 pixels.' : 'Panels are 16:9 aspect ratio.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {creatives.filter(c => c.format === creativePickerType).length === 0 ? (
              <div className="text-center py-8">
                <Image className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">
                  No {creativePickerType === 'BANNER' ? 'banners' : 'panels'} in your library yet
                </p>
                <Button onClick={() => { setCreativePickerOpen(false); saveDraftAndGoToCreatives(); }}>
                  <ImagePlus className="mr-2 h-4 w-4" />
                  Create New Creative
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {creatives.filter(c => c.format === creativePickerType).map(creative => (
                  <div
                    key={creative.id}
                    onClick={() => selectCreative(creative)}
                    className={`relative rounded-lg overflow-hidden border-2 cursor-pointer transition-all hover:border-primary ${
                      (creativePickerType === 'BANNER' && newCampaign.selectedBanner?.id === creative.id) ||
                      (creativePickerType === 'PANEL' && newCampaign.selectedPanel?.id === creative.id)
                        ? 'border-primary ring-2 ring-primary/30'
                        : 'border-border'
                    }`}
                    data-testid={`creative-option-${creative.id}`}
                  >
                    <div style={{ aspectRatio: creativePickerType === 'BANNER' ? '320/100' : '16/9' }}>
                      <img 
                        src={creative.file_url} 
                        alt={creative.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-2 bg-secondary/80">
                      <p className="text-sm font-medium truncate">{creative.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {creative.source === 'AI_GENERATED' ? '✨ AI Generated' : 
                         creative.source === 'URL_IMPORTED' ? '🔗 Imported' : '📤 Uploaded'}
                      </p>
                    </div>
                    {((creativePickerType === 'BANNER' && newCampaign.selectedBanner?.id === creative.id) ||
                      (creativePickerType === 'PANEL' && newCampaign.selectedPanel?.id === creative.id)) && (
                      <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setCreativePickerOpen(false)}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={() => { setCreativePickerOpen(false); saveDraftAndGoToCreatives(); }}>
              <ImagePlus className="mr-2 h-4 w-4" />
              Create New
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Modal */}
      <Dialog open={archiveModalOpen} onOpenChange={setArchiveModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Archive Campaign
            </DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to archive <strong>&ldquo;{campaignToArchive?.name}&rdquo;</strong>?
              <br /><br />
              <span className="text-muted-foreground">
                Archived campaigns cannot be un-archived, but they can be copied to create a new draft.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setArchiveModalOpen(false);
                setCampaignToArchive(null);
              }}
              disabled={archiving}
            >
              Cancel
            </Button>
            <Button 
              variant="default"
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={confirmArchive}
              disabled={archiving}
            >
              {archiving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Archive Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Location Targeting Modal */}
      <Dialog open={locationTargetingOpen} onOpenChange={setLocationTargetingOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Location Targeting
            </DialogTitle>
            <DialogDescription>
              Define where your ads will be shown. Target by your venue locations, states, postcodes, or specific suburbs.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="venues" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="venues" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Your Venues
              </TabsTrigger>
              <TabsTrigger value="custom" className="flex items-center gap-2">
                <Map className="h-4 w-4" />
                Custom Locations
              </TabsTrigger>
            </TabsList>
            
            {/* Your Venue Locations Tab */}
            <TabsContent value="venues" className="flex-1 overflow-y-auto mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Select which of your registered venue locations to target. Ads will show to passengers within the configured radius of these locations.
              </p>
              
              {locations.length === 0 ? (
                <div className="text-center py-8 border rounded-lg bg-secondary/30">
                  <Building2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground mb-3">No venue locations registered</p>
                  <Link to="/locations">
                    <Button size="sm">
                      <Plus className="mr-2 h-4 w-4" />
                      Add Location
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between pb-2 border-b">
                    <span className="text-sm font-medium">{locations.length} venue{locations.length !== 1 ? 's' : ''} available</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const allIds = locations.map(l => l.id);
                        const allSelected = allIds.every(id => newCampaign.targeting.location_ids.includes(id));
                        setNewCampaign(prev => ({
                          ...prev,
                          targeting: {
                            ...prev.targeting,
                            location_ids: allSelected ? [] : allIds
                          }
                        }));
                      }}
                    >
                      {locations.every(l => newCampaign.targeting.location_ids.includes(l.id)) ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  {locations.map(loc => (
                    <div 
                      key={loc.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        newCampaign.targeting.location_ids.includes(loc.id)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => {
                        setNewCampaign(prev => ({
                          ...prev,
                          targeting: {
                            ...prev.targeting,
                            location_ids: prev.targeting.location_ids.includes(loc.id)
                              ? prev.targeting.location_ids.filter(id => id !== loc.id)
                              : [...prev.targeting.location_ids, loc.id]
                          }
                        }));
                      }}
                    >
                      <Checkbox 
                        checked={newCampaign.targeting.location_ids.includes(loc.id)}
                        className="pointer-events-none"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{loc.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{loc.address}</p>
                      </div>
                      <Badge variant="outline" className="shrink-0 text-xs">
                        {loc.radius_m}m radius
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
            
            {/* Custom Locations Tab */}
            <TabsContent value="custom" className="flex-1 overflow-y-auto mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Target broader geographic areas by state, postcode, or suburb. Useful for reaching audiences beyond your venue locations.
              </p>
              
              {/* States Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">States</Label>
                <div className="grid grid-cols-4 gap-2">
                  {australianStates.map(state => (
                    <div
                      key={state.code}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                        newCampaign.targeting.states.includes(state.code)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => {
                        setNewCampaign(prev => ({
                          ...prev,
                          targeting: {
                            ...prev.targeting,
                            states: prev.targeting.states.includes(state.code)
                              ? prev.targeting.states.filter(s => s !== state.code)
                              : [...prev.targeting.states, state.code]
                          }
                        }));
                      }}
                    >
                      <Checkbox 
                        checked={newCampaign.targeting.states.includes(state.code)}
                        className="pointer-events-none h-4 w-4"
                      />
                      <span>{state.code}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Suburb/Postcode Search */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Suburbs & Postcodes</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search suburbs or postcodes..."
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    className="pl-9"
                  />
                  {searchingLocations && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
                
                {/* Search Results */}
                {locationSearchResults.length > 0 && (
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {locationSearchResults.map((result, idx) => {
                      const isSuburbSelected = newCampaign.targeting.suburbs.includes(result.suburb);
                      const isPostcodeSelected = newCampaign.targeting.postcodes.includes(result.postcode);
                      
                      return (
                        <div
                          key={idx}
                          className={`flex items-center justify-between p-2 hover:bg-secondary/50 cursor-pointer border-b last:border-b-0 ${
                            isSuburbSelected || isPostcodeSelected ? 'bg-primary/5' : ''
                          }`}
                          onClick={() => {
                            if (result.suburb && !isSuburbSelected) {
                              setNewCampaign(prev => ({
                                ...prev,
                                targeting: {
                                  ...prev.targeting,
                                  suburbs: [...prev.targeting.suburbs, result.suburb],
                                  postcodes: result.postcode && !prev.targeting.postcodes.includes(result.postcode)
                                    ? [...prev.targeting.postcodes, result.postcode]
                                    : prev.targeting.postcodes
                                }
                              }));
                            }
                            setLocationSearch("");
                            setLocationSearchResults([]);
                          }}
                        >
                          <div>
                            <span className="font-medium text-sm">{result.suburb}</span>
                            {result.postcode && (
                              <span className="text-muted-foreground text-sm ml-2">{result.postcode}</span>
                            )}
                            {result.state && (
                              <Badge variant="outline" className="ml-2 text-xs">{result.state}</Badge>
                            )}
                          </div>
                          <Plus className="h-4 w-4 text-muted-foreground" />
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Selected Suburbs */}
                {(newCampaign.targeting.suburbs.length > 0 || newCampaign.targeting.postcodes.length > 0) && (
                  <div className="space-y-2 pt-2">
                    <Label className="text-xs text-muted-foreground">Selected Locations</Label>
                    <div className="flex flex-wrap gap-2">
                      {newCampaign.targeting.suburbs.map(suburb => (
                        <Badge 
                          key={suburb} 
                          variant="secondary"
                          className="cursor-pointer hover:bg-destructive/20"
                          onClick={() => {
                            setNewCampaign(prev => ({
                              ...prev,
                              targeting: {
                                ...prev.targeting,
                                suburbs: prev.targeting.suburbs.filter(s => s !== suburb)
                              }
                            }));
                          }}
                        >
                          {suburb}
                          <X className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                      {newCampaign.targeting.postcodes.filter(p => !newCampaign.targeting.suburbs.some(s => s.includes(p))).map(postcode => (
                        <Badge 
                          key={postcode} 
                          variant="outline"
                          className="cursor-pointer hover:bg-destructive/20"
                          onClick={() => {
                            setNewCampaign(prev => ({
                              ...prev,
                              targeting: {
                                ...prev.targeting,
                                postcodes: prev.targeting.postcodes.filter(p => p !== postcode)
                              }
                            }));
                          }}
                        >
                          {postcode}
                          <X className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          {/* Summary */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Current Selection</span>
              <span className="text-sm text-muted-foreground">{getLocationTargetingSummary()}</span>
            </div>
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setNewCampaign(prev => ({
                    ...prev,
                    targeting: {
                      ...prev.targeting,
                      location_ids: [],
                      states: [],
                      postcodes: [],
                      suburbs: []
                    }
                  }));
                }}
              >
                Clear All
              </Button>
              <Button onClick={() => setLocationTargetingOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CampaignPaymentModal
        open={paymentModal.open}
        onOpenChange={(open) => {
          setPaymentModal((p) => ({ ...p, open }));
          if (!open) {
            // Refresh so PAYMENT_FAILED campaigns show correctly if user closed without paying
            fetchData();
          }
        }}
        clientSecret={paymentModal.clientSecret}
        amount={paymentModal.amount}
        campaignName={paymentModal.campaignName}
        token={token}
        onSuccess={() => {
          setPaymentModal({ open: false, clientSecret: null, amount: 0, campaignName: "" });
          fetchData();
        }}
      />
    </div>
  );
}
